import type { eventWithTime } from '@carboncopyco/rrweb-types';
import {
  type Tour,
  type TourAspectRatio,
  type TourFrame,
  type TourStep,
  TourStepEngineEditorMessageType,
  type TourStepEnginePreviewAction,
  TourStepEnginePreviewMessageType,
} from '@carboncopyco/types';
import { Loading } from '@carboncopyco/ui';
import { Debug } from '@carboncopyco/utils';
import { scopedLogger, Scope } from '@carboncopyco/logger';
import { type DependencyList, type RefObject, useRef } from 'react';
import { useStepPosition } from '../../hooks/useStepPosition';

import { TourPlayer, type TourPlayerRef } from '../../components/TourPlayer';
import {
  useFrameChunks,
  usePlayableFrames,
  usePlayableSteps,
  useReplayerActions,
  useReplayerActiveState,
  useReplayerListeners,
  useReplayerReady,
} from '../../hooks/replayer';
import { useIsAnyFrameScreenshot } from '../../hooks/useIsAnyFrameScreenshot';
import { attachSession } from '../../utils/sessionForwarding';
import { sendNavigationMessageToParent } from '../../utils/tourStitching';

const debug = Debug.extend('ToursReplayerPreview');
const engineLogger = scopedLogger(Scope.TOUR_ENGINE);

interface ToursReplayerPreviewProps {
  uuid: string;
  events: eventWithTime[];
  frames: TourFrame[];
  tourSettings: Tour['draftSettings'] | Tour['tourSettings'];
  onPlayStep?: (stepData: {
    stepId: number | string;
    stepIndex: number;
    frameIndex: number;
    completed: boolean;
    eventType: string;
  }) => void;
  fileServiceApiToken: string;
  tourAspectRatio?: TourAspectRatio;
  fileServiceDynamicLink: string;
  mimesisStepEngineUrl: string;
  isDebugMode: boolean;
  tabId?: number;
  activeTabId?: number;
  rootElement?: RefObject<HTMLDivElement>;
  initFrameDeps?: DependencyList;
}

const isPreview = true;

export const ToursReplayerPreview = ({
  uuid,
  events,
  frames,
  onPlayStep,
  fileServiceApiToken,
  tourAspectRatio,
  tourSettings,
  fileServiceDynamicLink,
  mimesisStepEngineUrl,
  isDebugMode,
  tabId,
  activeTabId,
  rootElement,
  initFrameDeps,
}: ToursReplayerPreviewProps) => {
  const playableFrames = usePlayableFrames(uuid, frames, isPreview);
  const playableSteps = usePlayableSteps(playableFrames);
  const { activeFrame, activeStep } = useReplayerActiveState(
    playableFrames,
    uuid
  );

  const { isReady } = useReplayerReady(playableFrames, events, uuid);
  const { frameChunks, getCurrentFrameChunk } = useFrameChunks(
    frames,
    events,
    uuid,
    fileServiceApiToken
  );

  const playerRef = useRef<TourPlayerRef>(null);
  const isAnyFrameScreenshot = useIsAnyFrameScreenshot(frames);

  const {
    lastPlayedStep,
    incrementalStepNavigation,
    onPlayHandler,
    onPauseHandler,
    onTooltipDisplayed,
    refreshTooltip,
    playStep,
  } = useReplayerActions({
    isReady,
    playerRef,
    frameChunks,
    playableFrames,
    playableSteps,
    activeFrame,
    activeStep,
    settings: tourSettings,
    uuid,
    rootElement,
    isPreview,
    onStepChange: onPlayStep,
  });

  const onPostMessage = (event: {
    data: TourStepEnginePreviewAction;
  }) => {
    const { type, payload } = event.data;
    if (type) {
      debug('onPostMessage', { type, payload });
      engineLogger.info(`Tour preview event: ${type}`, payload);
    }
    methods[type]?.(payload);
  };

  useReplayerListeners({
    playableFrames,
    playableSteps,
    activeFrame,
    activeStep,
    lastPlayedStep,
    activeTabId,
    tabId,
    onPostMessageHandler: onPostMessage,
    refreshTooltip,
    incrementalStepNavigation,
  });

  const onRestartRequestHandler = () => {
    playStep(playableSteps[0].stepId);
  };

  const onExternalUrlHandler = (payload: {
    url?: string;
  }) => {
    if (!payload?.url) {
      return;
    }

    const targetUrl = attachSession(payload.url);
    window.open(targetUrl, '_blank');
  };
  const onCustomStepRequestHandler = (payload: {
    goToStepId: TourStep['stepId'];
  }) => {
    const stepToReplay = playableSteps.find(
      (playableStep) => playableStep.stepId === payload?.goToStepId
    );
    if (!stepToReplay) {
      return;
    }
    playStep(stepToReplay.stepId);
  };

  const onNextStepRequestHandler = (payload: {
    keyboardNavigation?: boolean;
  }) => {
    sendNavigationMessageToParent('next');
    incrementalStepNavigation('next', !!payload?.keyboardNavigation);
  };

  const onBackStepRequestHandler = (payload: {
    keyboardNavigation: boolean;
  }) => {
    sendNavigationMessageToParent('back');
    incrementalStepNavigation('back', !!payload?.keyboardNavigation);
  };

  const onTourEndedHandler = () => {
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'dsTourEnd' }, '*');
    }
  };

  const methods: Record<
    TourStepEnginePreviewMessageType,
    (payload: unknown) => void
  > = {
    [TourStepEngineEditorMessageType.onStepEngineReady]: () => refreshTooltip(),
    [TourStepEnginePreviewMessageType.onRestartTour]: () => {
      sendNavigationMessageToParent('restart');
      onRestartRequestHandler();
    },
    [TourStepEnginePreviewMessageType.onExternalUrl]: onExternalUrlHandler,
    [TourStepEnginePreviewMessageType.onNextStep]: onNextStepRequestHandler,
    [TourStepEnginePreviewMessageType.onBackStep]: onBackStepRequestHandler,
    [TourStepEnginePreviewMessageType.onCustomStep]: onCustomStepRequestHandler,
    [TourStepEnginePreviewMessageType.onTooltipDisplayed]: onTooltipDisplayed,
    [TourStepEnginePreviewMessageType.onTourEnd]: onTourEndedHandler,
  };

  const { isTourFirstStep, isTourLastStep } = useStepPosition({
    playableSteps,
    activeStep,
  });

  return isReady ? (
    <div className="w-full h-full">
      <TourPlayer
        ref={playerRef}
        events={events}
        fileServiceApiToken={fileServiceApiToken}
        isBuffering={false}
        onPauseHandler={onPauseHandler}
        onPlayHandler={onPlayHandler}
        incrementalStepNavigation={incrementalStepNavigation}
        onRestartHandler={onRestartRequestHandler}
        editorMode={false}
        tourAspectRatio={tourAspectRatio}
        activeFrameId={activeFrame?.id}
        activeStep={activeStep}
        playableFrames={playableFrames}
        fileServiceDynamicLink={fileServiceDynamicLink}
        mimesisStepEngineUrl={mimesisStepEngineUrl}
        isDebugMode={isDebugMode}
        tourSettings={tourSettings}
        isAnyFrameScreenshot={isAnyFrameScreenshot}
        frameChunk={getCurrentFrameChunk(activeFrame)}
        initFrameDeps={initFrameDeps}
        activeTabId={activeTabId}
        isTourFirstStep={isTourFirstStep}
        isTourLastStep={isTourLastStep}
        rootElement={rootElement}
      />
    </div>
  ) : (
    <div className="w-full h-full flex justify-center items-center">
      <Loading />
    </div>
  );
};
